import { useEffect, useCallback, useRef } from 'react';

const MOTIVATIONAL_MESSAGES = {
  morning: [
    "Rise and shine! Your future self is counting on today's habits.",
    "Good morning, champion! Time to set your intentions.",
    "A new day, a new opportunity to become 1% better.",
    "The early bird gets the worm. Let's crush today!",
    "Your morning routine is calling. Answer it!"
  ],
  windDown: [
    "Time to wind down. How was your day?",
    "Evening check-in: Have you reflected on today's wins?",
    "The day's not over yet - time for your evening routine!",
    "Prep for tomorrow, rest well tonight.",
    "Almost there! Complete your evening habits."
  ],
  bedtime: [
    "Time to rest, warrior. Sleep is when you level up.",
    "Log your day and get some quality sleep!",
    "Your body and mind need rest. Time to wrap up.",
    "Tomorrow's success starts with tonight's rest.",
    "Close the day strong. Rest well!"
  ]
};

export function useReminders(settings, todayData) {
  const lastNotificationRef = useRef({});

  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }
    
    if (Notification.permission === 'granted') {
      return true;
    }
    
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    
    return false;
  }, []);

  const sendNotification = useCallback((title, body, tag) => {
    if (Notification.permission === 'granted') {
      const now = Date.now();
      const lastSent = lastNotificationRef.current[tag] || 0;
      
      // Prevent duplicate notifications within 5 minutes
      if (now - lastSent < 5 * 60 * 1000) {
        return;
      }
      
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag,
        requireInteraction: false
      });
      
      lastNotificationRef.current[tag] = now;
    }
  }, []);

  const getRandomMessage = useCallback((type) => {
    const messages = MOTIVATIONAL_MESSAGES[type];
    return messages[Math.floor(Math.random() * messages.length)];
  }, []);

  const checkReminders = useCallback(() => {
    if (!settings.notificationsEnabled) return;
    
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5);
    
    // Morning reminder
    if (settings.wakeUpReminder && currentTime === settings.wakeUpReminder) {
      if (!todayData.wakeUpTime || !todayData.gratitude) {
        sendNotification(
          'Morning Routine',
          getRandomMessage('morning'),
          'morning-reminder'
        );
      }
    }
    
    // Wind down reminder
    if (settings.windDownReminder && currentTime === settings.windDownReminder) {
      const completion = calculateQuickCompletion(todayData);
      if (completion < 70) {
        sendNotification(
          'Wind Down Time',
          getRandomMessage('windDown'),
          'winddown-reminder'
        );
      }
    }
    
    // Bedtime reminder
    if (settings.bedtimeReminder && currentTime === settings.bedtimeReminder) {
      if (!todayData.proudOf || !todayData.dayRating) {
        sendNotification(
          'Bedtime Reflection',
          getRandomMessage('bedtime'),
          'bedtime-reminder'
        );
      }
    }
  }, [settings, todayData, sendNotification, getRandomMessage]);

  const calculateQuickCompletion = (data) => {
    const habits = [
      data.brushedTeethAM,
      data.brushedTeethPM,
      data.gym,
      data.nature,
      data.readPages >= 10,
      data.waterOunces >= 64
    ];
    return (habits.filter(Boolean).length / habits.length) * 100;
  };

  useEffect(() => {
    const interval = setInterval(checkReminders, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [checkReminders]);

  return {
    requestNotificationPermission,
    sendNotification
  };
}

export default useReminders;
